/**
 * The ordering context body, parsed (ORDR-41; `site_id` kept since ORDR-42,
 * `node_type` since ORDR-194).
 *
 * A malformed 200 is a failure, not an empty list: reading a missing `nodes` as
 * `[]` would show the notice for a server that answered nonsense.
 */

import type { ActingNode } from "@pirouz-ca/ui-components";

/**
 * The node types paio-ordering's context sends. `RESELLER` (ORDR-193) is a
 * value of that document only: a reseller firm owns no brand, location or site
 * in ordering, so nothing that needs one can be acted on at it.
 */
export type ContextNodeType = "RESELLER" | "BRAND" | "GROUP" | "REGION" | "FRANCHISEE" | "LOCATION" | "SITE";

export interface ParsedContext {
  readonly nodes: readonly ActingNode[];
  /** The Site each node trades at. A node with no current Site is absent. */
  readonly siteIdByNode: Readonly<Record<string, string>>;
  /** Each node's type. A node whose type was not sent, or is not one we know, is absent. */
  readonly nodeTypeByNode: Readonly<Record<string, ContextNodeType>>;
}

const NODE_TYPES: ReadonlySet<string> = new Set<ContextNodeType>([
  "RESELLER",
  "BRAND",
  "GROUP",
  "REGION",
  "FRANCHISEE",
  "LOCATION",
  "SITE",
]);

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((entry) => typeof entry === "string");
}

/** The context body, or `null` when it is not one. */
export function parseContextNodes(body: unknown): ParsedContext | null {
  const data = (body as { data?: unknown } | null)?.data;
  const nodes = (data as { nodes?: unknown } | null | undefined)?.nodes;
  if (!Array.isArray(nodes)) {
    return null;
  }
  const parsed: ActingNode[] = [];
  const siteIdByNode: Record<string, string> = {};
  const nodeTypeByNode: Record<string, ContextNodeType> = {};
  for (const entry of nodes as unknown[]) {
    const candidate = entry as
      | { node_id?: unknown; node_type?: unknown; label?: unknown; capabilities?: unknown; site_id?: unknown }
      | null;
    if (
      typeof candidate?.node_id !== "string" ||
      typeof candidate.label !== "string" ||
      !isStringArray(candidate.capabilities)
    ) {
      return null;
    }
    parsed.push({ id: candidate.node_id, name: candidate.label, capabilities: candidate.capabilities });
    // ORDR-42: `site_id` is what the kitchen board polls the change feed
    // with. paio-ordering sends null for a node with no current Site, which
    // is a node with no kitchen, not a malformed body.
    if (typeof candidate.site_id === "string" && candidate.site_id !== "") {
      siteIdByNode[candidate.node_id] = candidate.site_id;
    }
    // ORDR-194: an unknown type is not a malformed body. It is left out, and the
    // node is then treated exactly as every node was before types were read.
    if (typeof candidate.node_type === "string" && NODE_TYPES.has(candidate.node_type)) {
      nodeTypeByNode[candidate.node_id] = candidate.node_type as ContextNodeType;
    }
  }
  return { nodes: parsed, siteIdByNode, nodeTypeByNode };
}
